/**
 * ARVEXIS Shared Memories Component
 * ==================================
 * 
 * Phase F: Memory Transparency Interface
 * 
 * Design Philosophy:
 * - Calm, relationship-centered design
 * - "Shared Memories" reinforces consent, collaboration, and transparency
 * - These are memories the user has chosen to share
 * - Every memory can be reviewed, edited, forgotten, and explained
 * 
 * Constitutional: Memory serves the relationship, not to prove recall.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, X, Trash2, Plus, Info, 
  MessageCircle, AlertTriangle, Check, Loader2,
  ChevronRight, ChevronDown, ChevronLeft, Sparkles, Pencil, MoreHorizontal, Moon
} from 'lucide-react';
import DreamsShell from './DreamsShell';
import useFullScreenSurface from '../hooks/useFullScreenSurface';
import { useMemoryAdmission, MemoryAdmissionLine } from './MemoryAdmission';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// PROVENANCE LABEL — where she received it, in one word.
//
// Founder ruling: CATEGORY and PROVENANCE are two axes. Category is carried by the surface's
// own organisation; this label carries only provenance, and only from the `source` the backend
// already stores and publishes. It infers nothing. Where there is no source — the one inferred
// thread, every suppression marker — there is NO LABEL, because a guess about where something
// came from is the one thing this surface must never make. No "Unknown", no "Other".
//
// A remember item the member asked for during a Talk shows "Talk" here and still says "You
// asked me to remember this" in Why: two different truthful axes, neither overriding the other.
// "You asked" belongs only to records whose stored source is the explicit request itself (api).
// =============================================================================
const PROVENANCE_LABEL = {
  api: { en: 'You asked', es: 'Tú lo pediste' },
  talk: { en: 'Talk', es: 'Talk' },
  message: { en: 'Message', es: 'Mensaje' },
  dream: { en: 'Dream', es: 'Sueño' },
};

const provenanceLabel = (source, lang) => {
  const s = String(source || '').toLowerCase();
  let key = null;
  if (s === 'api') key = 'api';
  else if (s === 'talk' || s.startsWith('conversation:talk')) key = 'talk';
  else if (s.startsWith('conversation:chat') || s.startsWith('conversation:daily_companion')) key = 'message';
  else if (s === 'dream' || s.startsWith('conversation:dream')) key = 'dream';
  if (!key) return null;
  return PROVENANCE_LABEL[key][lang === 'es' ? 'es' : 'en'];
};

const SourceLabel = ({ label, testId = 'memory-source-label' }) => (
  label ? (
    <span
      className="whitespace-nowrap"
      style={{ fontFamily: "'Inter', system-ui, sans-serif", color: 'rgba(244, 239, 231, 0.34)' }}
      data-testid={testId}
    >
      {label}
    </span>
  ) : null
);

// Phase B — date and provenance together, restrained, secondary to the member's own words.
// The date is only ever a date the record actually carries; when there is none, only the
// provenance shows, and when there is neither, the line does not exist.
const formatWhen = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  try {
    return d.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US',
                                { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return String(iso).slice(0, 10);
  }
};

const MetaLine = ({ when, label, testId = 'memory-source-label', whenTestId = 'memory-when' }) => (
  (when || label) ? (
    <div className="flex items-center gap-1.5 mt-1.5 text-[11.5px]" data-testid="memory-meta">
      {when && (
        <span className="whitespace-nowrap"
              style={{ fontFamily: "'Inter', system-ui, sans-serif",
                       color: 'rgba(244, 239, 231, 0.34)' }}
              data-testid={whenTestId}>
          {when}
        </span>
      )}
      {when && label && (
        <span aria-hidden="true" style={{ color: 'rgba(244, 239, 231, 0.2)' }}>·</span>
      )}
      <SourceLabel label={label} testId={testId} />
    </div>
  ) : null
);

const CardIcon = ({ children, testId = 'memory-icon' }) => (
  <div
    className="mt-0.5 w-9 h-9 shrink-0 rounded-[12px] flex items-center justify-center"
    style={{ background: 'rgba(255, 255, 255, 0.045)', border: '1px solid rgba(244, 239, 231, 0.08)' }}
    data-testid={testId}
  >
    {children}
  </div>
);

// =============================================================================
// MEMORY CARD — Individual shared memory item
// =============================================================================
const MemoryCard = ({ 
  memory, 
  type, // 'remember' | 'avoid' | 'thread'
  lang, 
  onForget,
  onCorrect,
  isDeleting,
  // 3.9 — the row the member tapped through to see, so "what changed" is inspectable.
  isFocused = false
}) => {
  const [showReason, setShowReason] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const getIcon = () => {
    if (type === 'remember') return <Heart className="w-4 h-4 text-rose-400" />;
    if (type === 'avoid') return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    if (type === 'thread') return <Sparkles className="w-4 h-4 text-cyan-400" />;
    return <MessageCircle className="w-4 h-4 text-[#D4AF37]" />;
  };

  // 6C-11d (Founder ruling 5) — "Active thread" was an internal class name and was removed.
  // Phase A (Founder ruling) — the Remembers / Avoids chip was CATEGORY, which this surface's
  // own organisation already carries, so the chip now carries PROVENANCE instead, and only
  // where the stored source supports it.
  const sourceLabel = provenanceLabel(memory.source, lang);
  const bodyText = memory.content || memory.topic || memory.thread || '';
  const isLong = String(bodyText).length > 110;
  const recordId = memory.item_id || memory.thread_id || memory.milestone_id || undefined;

  const menuItemClass =
    'w-full flex items-center gap-2.5 px-3.5 min-h-[44px] text-left text-[13.5px] ' +
    'transition-colors disabled:opacity-40 outline-none focus-visible:bg-white/[0.08]';
  
  // 6C-3 (Founder ruling) — EVIDENCE ONLY. The generated rationale that used to live here was
  // removed: it pattern-matched on `reason`/`source` text and produced a sentence nobody had
  // said. Everything below is a field the backend stores and now publishes. Where a fact does
  // not exist, this says so plainly rather than filling the gap.
  //
  // 6C-11 (Founder device-review correction) — one sentence per deterministic provenance class
  // published by the backend. The class is chosen from stored evidence only; this map does no
  // selection of its own, states no source/date/quote/count that the class does not already
  // establish, and gives materially different provenance materially different wording.
  const PROVENANCE_LINE = {
    you_asked_me_to_remember_this: {
      en: 'You asked me to remember this.',
      es: 'Me pediste que recordara esto.',
    },
    you_told_me_this_in_a_conversation: {
      en: 'You told me this in a conversation.',
      es: 'Me dijiste esto en una conversación.',
    },
    repeated_in_our_conversations: {
      en: 'This came up several times in our conversations.',
      es: 'Esto surgió varias veces en nuestras conversaciones.',
    },
    you_mentioned_this_during_a_talk: {
      en: 'You mentioned this during a Talk.',
      es: 'Lo mencionaste durante un Talk.',
    },
    from_your_dream_journal: {
      en: 'This appeared in your Dream Journal.',
      es: 'Esto apareció en tu Diario de Sueños.',
    },
    repeated_dream_journal_evidence: {
      en: 'This appeared in more than one Dream Journal entry.',
      es: 'Esto apareció en más de una entrada de tu Diario de Sueños.',
    },
    you_corrected_this_now_current: {
      en: 'You corrected this, and this is what I now treat as current.',
      es: 'Lo corregiste, y esto es lo que ahora trato como vigente.',
    },
    source_detail_unavailable: {
      en: 'Source detail is unavailable for this one.',
      es: 'No hay detalle de origen disponible para esto.',
    },
  };
  const said = (iso) => (iso ? String(iso).slice(0, 10) : null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`px-3.5 py-3 rounded-[16px] border transition-colors ${
        isFocused ? 'border-[#E8C58F]/50' : 'border-[#F4EFE7]/[0.07] hover:border-[#E8C58F]/26'
      }`}
      style={{ background: 'rgba(255, 255, 255, 0.028)' }}
      data-memory-id={memory.item_id || memory.thread_id || memory.milestone_id || undefined}
      data-testid={isFocused ? 'memory-card-focused' : `memory-card-${type}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <CardIcon>{getIcon()}</CardIcon>
          <div className="flex-1 min-w-0">
            <p
              className={`text-[16px] min-[1200px]:text-[17px] leading-snug ${
                isLong && !expanded ? 'line-clamp-2' : ''
              }`}
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#F4EFE7' }}
              data-testid="memory-text"
            >
              {bodyText}
            </p>

            {/* Their own words are never truncated away — only folded, with a way back. */}
            {isLong && (
              <button
                onClick={() => setExpanded(e => !e)}
                className="mt-1 text-[12.5px] transition-colors"
                style={{ color: 'rgba(244, 239, 231, 0.42)' }}
                aria-expanded={expanded}
                data-testid="memory-text-expand"
              >
                {expanded
                  ? (lang === 'es' ? 'Mostrar menos' : 'Show less')
                  : (lang === 'es' ? 'Mostrar todo' : 'Show all')}
              </button>
            )}

            {/* Provenance and the record's own date — restrained, never louder than the words */}
            <MetaLine
              when={formatWhen(memory.added_at || memory.first_mentioned, lang)}
              label={sourceLabel}
            />
            <AnimatePresence>
              {showReason && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 text-xs text-white/55 space-y-1"
                  data-testid="memory-why-panel"
                >
                  <p data-testid="memory-why-provenance">
                    {(PROVENANCE_LINE[memory.provenance] || PROVENANCE_LINE.source_detail_unavailable)[lang === 'es' ? 'es' : 'en']}
                  </p>

                  {memory.your_words ? (
                    <p data-testid="memory-why-quote">
                      <span className="text-white/40">{lang === 'es' ? 'Tus palabras: ' : 'Your words: '}</span>
                      <span style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>“{memory.your_words}”</span>
                    </p>
                  ) : (
                    <p className="text-white/40" data-testid="memory-why-no-quote">
                      {lang === 'es'
                        ? 'No hay una cita tuya guardada para esto.'
                        : 'There is no saved quote of yours behind this one.'}
                    </p>
                  )}

                  {said(memory.first_mentioned || memory.added_at) && (
                    <p className="text-white/40" data-testid="memory-why-dates">
                      {lang === 'es' ? 'Primera vez: ' : 'First heard: '}
                      {said(memory.first_mentioned || memory.added_at)}
                      {said(memory.last_mentioned)
                        ? `${lang === 'es' ? ' · Más reciente: ' : ' · Most recent: '}${said(memory.last_mentioned)}`
                        : ''}
                    </p>
                  )}

                  {memory.occurrences_total > 0 && (
                    <div data-testid="memory-why-occurrences">
                      <p className="text-white/40">
                        {lang === 'es'
                          ? `Dicho en ${memory.occurrences_total} ocasión(es) distintas. Las 3 más recientes:`
                          : `Said on ${memory.occurrences_total} separate occasion(s). The 3 most recent:`}
                      </p>
                      <ul className="pl-3 list-disc">
                        {(memory.occurrences || []).map((o, i) => (
                          <li key={i}>
                            {said(o.said_at)}{o.surface ? ` · ${o.surface}` : ''}
                            {o.your_words
                              ? ` · “${o.your_words}”`
                              : (lang === 'es' ? ' · sin cita guardada' : ' · no saved quote')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 6C-4 — correction history, and what it used to say */}
                  {memory.corrected_at && (
                    <p className="text-amber-400/70" data-testid="memory-why-corrected">
                      {lang === 'es' ? 'Lo corregiste el ' : 'You corrected this on '}{said(memory.corrected_at)}.
                    </p>
                  )}
                  {(memory.superseded_values || []).length > 0 ? (
                    <ul className="pl-3 list-disc text-white/40" data-testid="memory-why-prior">
                      {memory.superseded_values.map((prior, i) => (
                        <li key={i}>
                          {lang === 'es' ? 'Antes decía: ' : 'It used to say: '}“{prior.value}”
                          {said(prior.superseded_at) ? ` (${said(prior.superseded_at)})` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : memory.corrected_at ? (
                    <p className="text-white/40" data-testid="memory-why-prior-unavailable">
                      {lang === 'es'
                        ? 'No guardamos la versión anterior de esta corrección.'
                        : 'The previous version of this one was not kept.'}
                    </p>
                  ) : null}
                  {memory.superseded_evidence && (
                    <p className="text-white/40" data-testid="memory-why-superseded">
                      {lang === 'es'
                        ? 'Una corrección posterior reemplazó la evidencia guardada aquí.'
                        : 'A later correction superseded the evidence kept here.'}
                    </p>
                  )}
                  {/* 6C-11b — the member's own stated period has passed, so this is no longer
                      treated as currently true. Nothing was deleted. Their timing words are the
                      evidence, and they are shown. */}
                  {memory.no_longer_current && (
                    <p className="text-white/40" data-testid="memory-why-not-current">
                      {memory.stated_timing
                        ? (lang === 'es'
                            ? `Lo situaste en “${memory.stated_timing}”, y ese momento ya pasó, así que no lo trato como algo actual.`
                            : `You placed this at “${memory.stated_timing}”, and that time has passed, so I no longer treat it as current.`)
                        : (lang === 'es'
                            ? 'El momento que describiste ya pasó, así que no lo trato como algo actual.'
                            : 'The time you described has passed, so I no longer treat this as current.')}
                    </p>
                  )}
                  {(memory.linked_dreams || []).length > 0 && (
                    <p className="text-cyan-300/60" data-testid="memory-why-dream">
                      {lang === 'es'
                        ? `Conectado con ${memory.linked_dreams.length} sueño(s) que registraste.`
                        : `Connected to ${memory.linked_dreams.length} dream(s) you recorded.`}
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Phase A — the row's own actions, behind one affordance. Each keeps its exact test id
            and acts on this record only; nothing is normalised across record types. */}
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="relative z-50 w-11 h-11 flex items-center justify-center rounded-full hover:bg-white/10 text-[#A8B3BF] hover:text-[#F4EFE7] transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
            title={lang === 'es' ? 'Opciones' : 'Options'}
            aria-label={lang === 'es' ? 'Opciones de este recuerdo' : 'Options for this memory'}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="memory-actions-btn"
            data-memory-id={recordId}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
                data-testid="memory-actions-backdrop"
              />
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-50 w-[236px] py-1 rounded-[14px] overflow-hidden"
                style={{
                  background: 'rgba(10, 16, 24, 0.97)',
                  border: '1px solid rgba(244, 239, 231, 0.12)',
                  boxShadow: '0 18px 40px rgba(0, 0, 0, 0.5)',
                }}
                data-testid="memory-actions-menu"
                data-memory-id={recordId}
              >
                <button
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setShowReason(r => !r); }}
                  className={`${menuItemClass} text-[#F4EFE7]/82 hover:bg-white/[0.06]`}
                  data-testid="memory-why-toggle"
                >
                  <Info className="w-3.5 h-3.5 text-[#A8B3BF]" />
                  {lang === 'es' ? '¿Por qué recuerdo esto?' : 'Why do I remember this?'}
                </button>

                {onCorrect && (
                  <button
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); onCorrect(memory); }}
                    disabled={isDeleting}
                    className={`${menuItemClass} text-[#F4EFE7]/82 hover:bg-white/[0.06] hover:text-amber-400`}
                    title={lang === 'es' ? 'Corregir esto' : "That's not right"}
                    data-testid="memory-correct-btn"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    {lang === 'es' ? 'Corregir esto' : "That's not right"}
                  </button>
                )}

                <button
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onForget(memory); }}
                  disabled={isDeleting}
                  className={`${menuItemClass} text-rose-400/80 hover:bg-white/[0.06] hover:text-rose-400`}
                  title={lang === 'es' ? 'Olvidar esto' : 'Forget this'}
                  data-testid="memory-forget-btn"
                >
                  {isDeleting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  {lang === 'es' ? 'Olvidar esto' : 'Forget this'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// FORGET CONFIRMATION — Phase 6. A UI safety step in front of the existing Forget action, so a
// memory is never dropped on a single accidental tap. It changes no retention contract: the
// conversation, Talk or Dream the memory came from is not deleted by this action.
// =============================================================================
const ForgetConfirm = ({ lang, isWorking, onCancel, onConfirm }) => {
  const copy = lang === 'es'
    ? {
        title: '¿Olvidar este recuerdo?',
        body: 'Arvexis dejará de usar esto como algo que recuerda de ti. La conversación, '
            + 'el Talk o el Sueño de donde vino no se elimina con esta acción.',
        cancel: 'Cancelar',
        confirm: 'Olvidar recuerdo',
      }
    : {
        title: 'Forget this memory?',
        body: 'Arvexis will stop using this as something it remembers about you. The original '
            + 'conversation, Talk, or Dream it came from is not deleted by this action.',
        cancel: 'Cancel',
        confirm: 'Forget memory',
      };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-[200] flex items-center justify-center px-5"
      style={{ background: 'rgba(5, 7, 12, 0.72)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      data-testid="memory-forget-confirm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 380, padding: '28px 24px 24px', borderRadius: 22,
          background: 'linear-gradient(180deg, #0B1020 0%, #070A12 100%)',
          border: '1px solid rgba(244, 239, 231, 0.10)',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.55)',
        }}
      >
        <h2
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 27,
                   lineHeight: 1.18, fontWeight: 500, color: '#F4EFE7', margin: 0 }}
          data-testid="memory-forget-confirm-title"
        >
          {copy.title}
        </h2>
        <p
          style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(244, 239, 231, 0.6)',
                   margin: '14px 0 24px' }}
          data-testid="memory-forget-confirm-body"
        >
          {copy.body}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ flex: 1, height: 48, borderRadius: 999, background: 'rgba(255,255,255,0.035)',
                     border: '1px solid rgba(244, 239, 231, 0.14)', color: '#F4EFE7',
                     fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
            data-testid="memory-forget-cancel"
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isWorking}
            style={{ flex: 1, height: 48, borderRadius: 999, background: 'rgba(224, 106, 106, 0.12)',
                     border: '1px solid rgba(224, 106, 106, 0.45)', color: '#E9A3A3',
                     fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
            data-testid="memory-forget-confirm-btn"
          >
            {copy.confirm}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// CORRECTION SHEET — 6C-5. Replaces window.prompt(), which was a system dialog, unstyled and
// untestable. Same endpoint, real surface.
// =============================================================================
const CorrectionSheet = ({ memory, lang, onCancel, onSubmit, isSaving }) => {
  const current = memory.content || memory.topic || memory.thread || memory.summary || '';
  const [value, setValue] = useState(current);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
         style={{ background: 'rgba(7, 20, 33, 0.9)' }} data-testid="memory-correction-sheet">
      <div className="w-full max-w-md rounded-[18px] p-5"
           style={{ background: 'rgba(7, 20, 33, 0.98)', border: '1px solid rgba(128,149,166,0.28)' }}>
        <p className="text-[15px] mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#F4EFE7' }}>
          {lang === 'es' ? '¿Cuál es la versión correcta?' : "What's the correct version?"}
        </p>
        <p className="text-xs text-white/40 mb-3">
          {lang === 'es'
            ? 'Guardaremos lo que digas y conservaremos lo que decía antes, para que puedas ver qué cambió.'
            : "We'll save what you say and keep what it said before, so you can see what changed."}
        </p>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={3}
          className="w-full rounded-xl p-3 text-sm text-white"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.14)' }}
          data-testid="memory-correction-input"
        />
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onSubmit(current, value.trim())}
            disabled={isSaving || !value.trim() || value.trim() === current}
            className="flex-1 h-11 rounded-xl text-sm disabled:opacity-40"
            style={{ background: 'rgba(232,197,143,0.16)', border: '1px solid rgba(232,197,143,0.4)', color: '#E8C58F' }}
            data-testid="memory-correction-save"
          >
            {isSaving ? (lang === 'es' ? 'Guardando...' : 'Saving...') : (lang === 'es' ? 'Guardar' : 'Save')}
          </button>
          <button onClick={onCancel} disabled={isSaving}
                  className="flex-1 h-11 rounded-xl text-sm text-white/60"
                  style={{ border: '1px solid rgba(255,255,255,0.14)' }}
                  data-testid="memory-correction-cancel">
            {lang === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// WHAT KEEPS COMING BACK — 6C-8. The recurrence the member's own dream record establishes,
// with their verdict on it. No internal store name, no score, no numeric confidence.
// =============================================================================
const PatternCard = ({ pattern, lang, onVerdict, isBusy }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuItemClass =
    'w-full flex items-center gap-2.5 px-3.5 min-h-[44px] text-left text-[13.5px] ' +
    'transition-colors disabled:opacity-40 outline-none focus-visible:bg-white/[0.08]';

  return (
  <div className="px-4 py-4 rounded-[18px] border"
       style={{ background: 'rgba(7, 20, 33, 0.78)', borderColor: 'rgba(128,149,166,0.22)' }}
       data-testid="pattern-card">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <CardIcon testId="pattern-icon"><Moon className="w-4 h-4 text-cyan-300/80" /></CardIcon>
        <div className="flex-1 min-w-0">
    <p className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#F4EFE7' }}>
      {pattern.what_recurred}
    </p>
    <p className="text-xs text-white/45 mt-1" data-testid="pattern-provenance">
      {lang === 'es'
        ? `Aparecio ${pattern.how_often} en los suenos que registraste · primera vez ${pattern.first_time} · mas reciente ${pattern.most_recent}`
        : `Came up ${pattern.how_often} in the dreams you recorded · first ${pattern.first_time} · most recent ${pattern.most_recent}`}
    </p>
    {(pattern.in_these_dreams || []).length > 0 && (
      <ul className="pl-3 list-disc text-xs text-white/35 mt-1">
        {pattern.in_these_dreams.map((d, i) => <li key={i}>{d.title} · {d.recorded}</li>)}
      </ul>
    )}
    {pattern.you_confirmed_this && (
      <p className="text-xs text-emerald-300/70 mt-1" data-testid="pattern-confirmed">
        {lang === 'es' ? 'Confirmaste que esto es real.' : 'You confirmed this one is real.'}
      </p>
    )}
    {pattern.you_corrected_this && (
      <p className="text-xs text-amber-400/70 mt-1" data-testid="pattern-corrected">
        {lang === 'es' ? 'Lo corregiste, así que no lo vuelvo a usar.' : "You corrected this, so I don't use it."}
      </p>
    )}
    <div className="mt-2">
      <SourceLabel label={provenanceLabel('dream', lang)} testId="pattern-source-label" />
    </div>        </div>
      </div>

      {/* Two truthful actions for this record only, behind one affordance. A corrected pattern
          has none, so it gets no menu rather than a decorative one. */}
      {!pattern.you_corrected_this && (
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="relative z-50 w-11 h-11 flex items-center justify-center rounded-full hover:bg-white/10 text-[#A8B3BF] hover:text-[#F4EFE7] transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
            title={lang === 'es' ? 'Opciones' : 'Options'}
            aria-label={lang === 'es' ? 'Opciones de este patrón' : 'Options for this pattern'}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="pattern-actions-btn"
            data-pattern-id={pattern.id}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}
                   data-testid="pattern-actions-backdrop" />
              <div role="menu"
                   className="absolute right-0 top-full mt-1 z-50 w-[210px] py-1 rounded-[14px] overflow-hidden"
                   style={{ background: 'rgba(10, 16, 24, 0.97)', border: '1px solid rgba(244, 239, 231, 0.12)', boxShadow: '0 18px 40px rgba(0, 0, 0, 0.5)' }}
                   data-testid="pattern-actions-menu"
                   data-pattern-id={pattern.id}>
                <button role="menuitem"
                        onClick={() => { setMenuOpen(false); onVerdict(pattern, 'confirm'); }}
                        disabled={isBusy}
                        className={`${menuItemClass} text-[#F4EFE7]/82 hover:bg-white/[0.06]`}
                        data-testid="pattern-confirm-btn">
                  <Check className="w-3.5 h-3.5 text-emerald-300/80" />
                  {lang === 'es' ? 'Sí, es real' : "Yes, that's real"}
                </button>
                <button role="menuitem"
                        onClick={() => { setMenuOpen(false); onVerdict(pattern, 'correct'); }}
                        disabled={isBusy}
                        className={`${menuItemClass} text-amber-400/80 hover:bg-white/[0.06] hover:text-amber-400`}
                        data-testid="pattern-correct-btn">
                  <Pencil className="w-3.5 h-3.5" />
                  {lang === 'es' ? 'No es así' : "That's not right"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

// =============================================================================
// HER OWN NOTES — 6C-10. Previously held and invisible. These are Arvexis's paraphrases from
// dreams, not the member's words: the card says so instead of implying a quote.
// =============================================================================
const NoteCard = ({ note, lang, onForget, isBusy }) => (
  <div className="px-4 py-4 rounded-[18px] border"
       style={{ background: 'rgba(7, 20, 33, 0.78)', borderColor: 'rgba(128,149,166,0.22)' }}
       data-testid="note-card">
    <div className="flex items-start gap-3">
      <CardIcon testId="note-icon">
        {note.provenance === 'arvexis_noted_from_dream'
          ? <Moon className="w-4 h-4 text-cyan-300/80" />
          : <MessageCircle className="w-4 h-4 text-[#D4AF37]" />}
      </CardIcon>
      <div className="flex-1 min-w-0">
    <p className="text-[15px]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#F4EFE7' }}>
      {note.content}
    </p>
    <p className="text-xs text-white/45 mt-1" data-testid="note-provenance">
      {/* 6C-11 — named source + the stored date, in the same register as the memory cards. */}
      {note.provenance === 'arvexis_noted_from_dream'
        ? (note.noted_at
            ? (lang === 'es'
                ? `Anotado de tu Diario de Sueños el ${String(note.noted_at).slice(0, 10)}`
                : `Noted from your Dream Journal on ${String(note.noted_at).slice(0, 10)}`)
            : (lang === 'es' ? 'Anotado de tu Diario de Sueños' : 'Noted from your Dream Journal'))
        : (note.noted_at
            ? (lang === 'es'
                ? `Anotado en una de nuestras conversaciones el ${String(note.noted_at).slice(0, 10)}`
                : `Noted in one of our conversations on ${String(note.noted_at).slice(0, 10)}`)
            : (lang === 'es'
                ? 'Anotado en una de nuestras conversaciones'
                : 'Noted in one of our conversations'))}
      {note.times_noted > 1
        ? (lang === 'es' ? ` · anotado ${note.times_noted} veces` : ` · noted ${note.times_noted} times`)
        : ''}
    </p>
    <p className="text-xs text-white/35 mt-1" data-testid="note-no-quote">
      {lang === 'es'
        ? 'Esto es mi nota, no tus palabras: no hay una cita guardada.'
        : 'This is my note, not your words — there is no saved quote.'}
    </p>
    {note.provenance === 'arvexis_noted_from_dream' && (
      <div className="mt-2">
        <SourceLabel label={provenanceLabel('dream', lang)} testId="note-source-label" />
      </div>
    )}
    {/* One truthful action for this record, so it stays inline — a menu behind a single
        action is an extra tap for nothing (Founder ruling). */}
    <button onClick={() => onForget(note)} disabled={isBusy}
            className="text-xs text-rose-400/70 hover:text-rose-400 min-h-[44px] mt-2"
            data-testid="note-forget-btn">
      {lang === 'es' ? 'Olvida esta nota' : 'Forget this note'}
    </button>
      </div>
    </div>
  </div>
);

// =============================================================================
// ADD MEMORY FORM — Let users explicitly add memories
// =============================================================================
const AddMemoryForm = ({ lang, onAdd, isAdding }) => {
  const [content, setContent] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    await onAdd(content.trim());
    setContent('');
    setShowForm(false);
  };
  
  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="w-full p-4 rounded-xl border border-dashed border-white/20 text-white/40 hover:text-white/60 hover:border-white/30 transition-all flex items-center justify-center gap-2"
        data-testid="add-memory-btn"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm">
          {lang === 'es' ? 'Agregar algo para recordar' : 'Add something to remember'}
        </span>
      </button>
    );
  }
  
  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="p-4 rounded-xl bg-white/5 border border-[#D4AF37]/30"
    >
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={
          lang === 'es' 
            ? '¿Qué te gustaría que Arvexis recuerde?' 
            : 'What would you like Arvexis to remember?'
        }
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
        style={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}
        rows={2}
        autoFocus
        data-testid="add-memory-input"
      />
      
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="px-3 py-1.5 rounded-full text-xs text-white/60 hover:text-white transition-colors"
        >
          {lang === 'es' ? 'Cancelar' : 'Cancel'}
        </button>
        <button
          type="submit"
          disabled={!content.trim() || isAdding}
          className="px-4 py-1.5 rounded-full text-xs font-medium bg-[#D4AF37] text-[#0F0F14] hover:bg-[#C8901E] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
          data-testid="add-memory-submit"
        >
          {isAdding ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          {lang === 'es' ? 'Guardar' : 'Save'}
        </button>
      </div>
    </motion.form>
  );
};

// =============================================================================
// SECTION HEADER — Section divider with count
// =============================================================================
// =============================================================================
// FILTER ROW — Phase B. The member's own organisation of their record.
//
// Founder ruling: these five carry CATEGORY, so the heavy section headings step back and the
// cards become the page. All is the default. No counts — being remembered should not feel
// tallied. No Profile, and provenance never becomes the taxonomy: Message / Talk / Dream stay
// on the card as a secondary label, because they answer a different question.
// =============================================================================
const FILTERS = [
  { id: 'all', en: 'All', es: 'Todo' },
  { id: 'remembers', en: 'Remembers', es: 'Recuerda' },
  { id: 'current', en: 'Right Now', es: 'Ahora mismo' },
  { id: 'dreams', en: 'Dreams', es: 'Sueños' },
  { id: 'forgotten', en: 'Forgotten', es: 'Olvidado' },
];

const FilterRow = ({ active, onChange, lang }) => (
  <div
    className="-mx-5 px-5 mb-3 overflow-x-auto"
    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
    data-testid="memory-filter-row"
  >
    <div className="flex items-center gap-2 w-max pb-1">
      {FILTERS.map(f => {
        const on = active === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            className="shrink-0 h-11 px-4 rounded-full text-[13.5px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/90 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent"
            style={on
              ? { background: '#F5D9AC', color: '#0A1018', fontWeight: 600,
                  border: '1px solid #F5D9AC' }
              : { background: 'rgba(255, 255, 255, 0.04)', color: 'rgba(244, 239, 231, 0.62)',
                  border: '1px solid rgba(244, 239, 231, 0.09)' }}
            aria-pressed={on}
            data-testid={`memory-filter-${f.id}`}
          >
            {lang === 'es' ? f.es : f.en}
          </button>
        );
      })}
    </div>
  </div>
);

const MemoryGroup = ({ id, title, subtitle, count, defaultOpen = false, activeFilter = 'all',
                      children }) => {
  // 6C-11c (Founder ruling 6) — "Remembered, not monitored."
  // Four member-facing groups instead of seven equal lists. Enduring memory is primary and open;
  // what is merely current, what came from dreams, and what she was asked to let go are
  // subordinate and collapsed. Nothing is removed and nothing is more than one tap away, every
  // row keeps what / why / Correct / Forget, and no internal store name, tier or class is shown.
  const [open, setOpen] = React.useState(defaultOpen);
  if (!count) return null;
  // Phase B — one filter at a time, and the filter itself names the group, so the heading steps
  // out of the way entirely. In All the label stays, but restrained: it is a boundary marker
  // (what came from dreams, what was let go), not a banner.
  if (activeFilter !== 'all' && activeFilter !== id) return null;
  const filtered = activeFilter === id;
  const isOpen = filtered || open;
  return (
    <div className="mb-4" data-testid={`memory-group-${id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-baseline justify-between gap-3 text-left group ${
          filtered ? 'sr-only' : 'py-1.5'
        }`}
        data-testid={`memory-group-toggle-${id}`}
        aria-expanded={isOpen}
      >
        <span>
          <span
            className="block"
            style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: '9.5px',
                     fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase',
                     color: 'rgba(232, 197, 143, 0.55)' }}
          >
            {title}
          </span>
          {subtitle && !filtered && (
            <span className="block mt-1" style={{ fontSize: '12px', lineHeight: 1.45,
                  color: 'rgba(244, 239, 231, 0.34)' }}>{subtitle}</span>
          )}
        </span>
        {/* Founder ruling: no numeric tally on the member-facing default view. A count makes
            being remembered feel monitored. The label and the disclosure remain; the number goes.
            `count` still governs whether a group has anything to show at all. */}
        <span className="whitespace-nowrap" style={{ fontSize: '12px', color: 'rgba(244, 239, 231, 0.28)' }}>
          <ChevronDown
            className={`inline-block w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {isOpen && <div className="mt-1" data-testid={`memory-group-body-${id}`}>{children}</div>}
    </div>
  );
};

const SectionHeader = ({ title, count, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-2 mt-4 first:mt-0">
    <Icon className="w-[15px] h-[15px]" style={{ color: 'rgba(232, 197, 143, 0.72)' }} strokeWidth={1.6} />
    <h3 
      className="text-[10px] font-semibold uppercase"
      style={{ fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: '0.2em', color: 'rgba(168, 179, 191, 0.85)' }}
    >
      {title}
    </h3>
    {count > 0 && (
      <span className="ml-auto text-[12px]" style={{ fontFamily: "'Inter', system-ui, sans-serif", color: 'rgba(168, 179, 191, 0.7)' }}>
        {count}
      </span>
    )}
  </div>
);

// =============================================================================
// MAIN SHARED MEMORIES COMPONENT
// =============================================================================
const SharedMemories = ({ 
  lang = 'en',
  onClose,
  // 3.9 — arrives when the member tapped a memory status to see what changed.
  focusItemId = null,
  activePath = '/profile'
}) => {
  useFullScreenSurface(true, onClose);
  const [isLoading, setIsLoading] = useState(true);
  const [rememberItems, setRememberItems] = useState([]);
  const [avoidItems, setAvoidItems] = useState([]);
  const [lifeThreads, setLifeThreads] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingForget, setPendingForget] = useState(null);
  const [error, setError] = useState(null);
  // 6C-5 / 6C-8 / 6C-10
  const [correcting, setCorrecting] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [notes, setNotes] = useState([]);
  const [forgottenNotes, setForgottenNotes] = useState([]);
  const [showAllAvoid, setShowAllAvoid] = useState(false);
  const [filter, setFilter] = useState('all');

  // 6C-11c (Founder ruling 6) — the split between enduring memory and merely-current context is
  // read from published evidence only: whether the member themselves placed it in a stretch of
  // time. No internal tier is consulted, published or shown.
  const currentThreads = lifeThreads.filter(t => t.stated_timing || t.no_longer_current);
  const enduringThreads = lifeThreads.filter(t => !t.stated_timing && !t.no_longer_current);

  // 3.9 — these acts used to confirm nothing but a silent list refresh.
  const { admission: memoryAdmission, check: checkMemoryAdmission } = useMemoryAdmission();

  // Load relationship context on mount
  useEffect(() => {
    loadMemories();
  }, []);

  // Bring the tapped-through row into view once the list exists. If the change was an
  // occurrence on an existing row, that row is what moved — so that is what we show.
  useEffect(() => {
    if (!focusItemId || isLoading) return;
    const row = document.querySelector(`[data-memory-id="${focusItemId}"]`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusItemId, isLoading]);

  const loadMemories = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/companion/relationship`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load memories');
      
      const data = await response.json();
      
      // Transform data for display
      // Backend returns full arrays (Phase F contract):
      //   remember_items: [{item_id, content, reason, source, added_at, user_confirmed}, ...]
      //   topics_to_avoid: [{item_id, topic, reason, added_at}, ...]
      //   active_life_threads: [{thread_id, topic, status, ...}, ...]
      setRememberItems(Array.isArray(data.remember_items) ? data.remember_items : []);
      setAvoidItems(Array.isArray(data.topics_to_avoid) ? data.topics_to_avoid : []);
      setLifeThreads(Array.isArray(data.active_life_threads) ? data.active_life_threads : []);
      setMilestones(Array.isArray(data.milestones) ? data.milestones : []);

      // 6C-8 / 6C-10 — the two stores that shaped her understanding while staying invisible.
      const [patternsRes, notesRes] = await Promise.all([
        fetch(`${API_URL}/api/companion/patterns`, { credentials: 'include' }),
        fetch(`${API_URL}/api/companion/notes`, { credentials: 'include' }),
      ]);
      if (patternsRes.ok) {
        const body = await patternsRes.json();
        setPatterns(Array.isArray(body.patterns) ? body.patterns : []);
      }
      if (notesRes.ok) {
        const body = await notesRes.json();
        setNotes(Array.isArray(body.notes) ? body.notes : []);
        setForgottenNotes(Array.isArray(body.forgotten) ? body.forgotten : []);
      }
      
    } catch (err) {
      console.error('Load memories error:', err);
      // 6C-9 (approved wording) — truthful: the load path is a GET and writes nothing.
      setError(lang === 'es'
        ? 'No se pudieron cargar tus recuerdos. No pudimos recuperarlos en este momento. No se ha cambiado nada. Intentalo de nuevo.'
        : "Couldn't load your memories. We couldn't retrieve them right now. Nothing has been changed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMemory = async (content) => {
    setIsAdding(true);
    
    try {
      const response = await fetch(`${API_URL}/api/companion/remember`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          content,
          reason: lang === 'es' ? 'Agregado manualmente' : 'Added manually'
        })
      });
      
      if (!response.ok) throw new Error('Failed to add memory');
      const data = await response.json();
      if (data.refused_sensitive) {
        setRefusal(data.message);  // V1 RC: nothing was saved, so say so instead of staying silent
        return;
      }
      setRefusal(null);
      
      // Refresh the list
      await loadMemories();
      checkMemoryAdmission();
      
    } catch (err) {
      console.error('Add memory error:', err);
      setError(lang === 'es' ? 'No se pudo guardar' : 'Could not save');
    } finally {
      setIsAdding(false);
    }
  };

  // Phase 6 — the destructive control asks first. The existing action is untouched; it simply
  // runs after the member confirms rather than on the first tap.
  const handleForget = (memory) => setPendingForget({ kind: 'memory', memory });
  const handleForgetNote = (note) => setPendingForget({ kind: 'note', memory: note });

  const executeForget = async (memory) => {
    const topic = memory.content || memory.topic || memory.thread || memory.summary;
    if (!topic) return;
    
    setDeletingId(memory.item_id || memory.milestone_id || topic);
    
    try {
      const response = await fetch(`${API_URL}/api/companion/forget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ topic })
      });
      
      if (!response.ok) throw new Error('Failed to forget');
      
      // Refresh the list
      await loadMemories();
      checkMemoryAdmission();
      
    } catch (err) {
      console.error('Forget error:', err);
      setError(lang === 'es' ? 'No se pudo olvidar' : 'Could not forget');
    } finally {
      setDeletingId(null);
    }
  };

  // R7: wire the existing Correct capability. Minimal control, no memory redesign.
  const handleCorrect = (memory) => setCorrecting(memory);

  const submitCorrection = async (incorrect, corrected) => {
    if (!incorrect || !corrected || corrected === incorrect) return;
    setDeletingId(incorrect);
    try {
      const response = await fetch(`${API_URL}/api/companion/correct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ incorrect, correct: corrected.trim() })
      });
      if (!response.ok) throw new Error('Failed to correct');
      await loadMemories();
      checkMemoryAdmission();
      setCorrecting(null);
    } catch (err) {
      console.error('Correct error:', err);
      setError(lang === 'es' ? 'No se pudo corregir. No se ha cambiado nada.'
                             : 'Could not correct that. Nothing has been changed.');
    } finally {
      setDeletingId(null);
    }
  };

  // 6C-8 — the member's verdict on what keeps coming back.
  const handlePatternVerdict = async (pattern, action) => {
    setDeletingId(pattern.id);
    try {
      const response = await fetch(`${API_URL}/api/companion/patterns/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pattern_id: pattern.id, action }),
      });
      if (!response.ok) throw new Error('verdict failed');
      await loadMemories();
    } catch (err) {
      console.error('Pattern verdict error:', err);
      setError(lang === 'es' ? 'No se pudo guardar tu respuesta. No se ha cambiado nada.'
                             : 'Could not save your answer. Nothing has been changed.');
    } finally {
      setDeletingId(null);
    }
  };

  // 6C-10 — forget one of her notes. The note goes; a marker stays so it cannot return.
  const executeForgetNote = async (note) => {
    setDeletingId(note.note_id);
    try {
      const response = await fetch(`${API_URL}/api/companion/notes/${note.note_id}/forget`, {
        method: 'POST', credentials: 'include',
      });
      if (!response.ok) throw new Error('forget failed');
      await loadMemories();
    } catch (err) {
      console.error('Forget note error:', err);
      setError(lang === 'es' ? 'No se pudo olvidar. No se ha cambiado nada.'
                             : 'Could not forget that. Nothing has been changed.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLiftNoteSuppression = async (item) => {
    setDeletingId(item.note_id);
    try {
      const response = await fetch(
        `${API_URL}/api/companion/notes/${item.note_id}/suppression/reverse`,
        { method: 'POST', credentials: 'include' });
      if (!response.ok) throw new Error('lift failed');
      await loadMemories();
    } catch (err) {
      console.error('Lift note suppression error:', err);
      setError(lang === 'es' ? 'No se pudo levantar. No se ha cambiado nada.'
                             : 'Could not lift that. Nothing has been changed.');
    } finally {
      setDeletingId(null);
    }
  };

  // R7: lift a suppression marker on purpose. Does not restore what was deleted.
  const handleLiftSuppression = async (item) => {
    const key = item.suppression_key;
    if (!key) return;
    setDeletingId(key);
    try {
      const response = await fetch(`${API_URL}/api/companion/suppression/reverse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ suppression_key: key })
      });
      if (!response.ok) throw new Error('Failed to lift');
      await loadMemories();
      checkMemoryAdmission();
    } catch (err) {
      console.error('Lift suppression error:', err);
      setError(lang === 'es' ? 'No se pudo levantar' : 'Could not lift');
    } finally {
      setDeletingId(null);
    }
  };

  const totalMemories = rememberItems.length + avoidItems.length + lifeThreads.length
    + milestones.length + patterns.length + notes.length + forgottenNotes.length;

  // Phase B — how much each filter actually holds. Used only to tell the member honestly when a
  // filter is empty; never displayed as a tally.
  const filterCounts = {
    all: totalMemories,
    remembers: rememberItems.length + milestones.length + enduringThreads.length,
    current: currentThreads.length,
    dreams: patterns.length + notes.length,
    forgotten: forgottenNotes.length + avoidItems.length,
  };
  const emptyFilterLine = {
    remembers: { en: "Nothing durable yet — this fills as you tell her things worth keeping.",
                 es: 'Aún nada duradero: esto se llena cuando le cuentas algo que vale guardar.' },
    current: { en: "Nothing you've placed in a particular stretch of time yet.",
               es: 'Aún nada que hayas situado en un momento concreto.' },
    dreams: { en: 'Nothing from your Dream Journal has come up more than once yet.',
              es: 'Aún nada de tu Diario de Sueños ha vuelto a aparecer.' },
    forgotten: { en: "You haven't asked her to forget anything.",
                 es: 'No le has pedido olvidar nada.' },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // the foreground is handed back at once: fading out cross-faded the still-mounted page beneath through this surface
      className="utility-overlay z-[150] utility-surface utility-surface--atmospheric"
      data-testid="shared-memories-modal"
    >
      <DreamsShell lang={lang} activePath={activePath}>
        {/* Header — mobile carries the back control and the wordmark; desktop has the sidebar */}
        <div
          className="flex items-center gap-4 px-5 account-mobile-header"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
            paddingBottom: '12px'
          }}
        >
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: 'transparent',
                     border: '1px solid rgba(232, 197, 143, 0.38)' }}
            aria-label={lang === 'es' ? 'Volver' : 'Back'}
            data-testid="shared-memories-close"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
          </button>
          <span
            className="account-wordmark"
            style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244, 239, 231, 0.8)',
                     margin: '0 auto', paddingRight: '44px' }}
            data-testid="shared-memories-wordmark"
          >
            ARVEXIS
          </span>
        </div>

        <div className="utility-head account-col account-col--wide">
          <div>
            <p className="utility-head__eyebrow" data-testid="shared-memories-eyebrow">
              {lang === 'es' ? 'RECUERDOS COMPARTIDOS' : 'SHARED MEMORIES'}
            </p>
            <h2
              className="text-[28px] leading-[34px] min-[1200px]:text-[40px] min-[1200px]:leading-[48px]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: '#F4EFE7' }}
              data-testid="shared-memories-title"
            >
              {lang === 'es' ? 'Recuerdos Compartidos' : 'Shared Memories'}
            </h2>
            <p
              className="text-[15px] leading-[22px] mt-2"
              style={{ fontFamily: "'Inter', system-ui, sans-serif", color: '#A8B3BF' }}
              data-testid="shared-memories-subtitle"
            >
              {lang === 'es'
                ? 'Lo que Arvexis recuerda de ti. Tú decides qué se queda.'
                : 'What Arvexis remembers about you. You decide what stays.'}
            </p>
          </div>
        </div>

        {memoryAdmission && (
          <div className="account-col account-col--wide" style={{ padding: '0 20px' }}>
            <MemoryAdmissionLine admission={memoryAdmission} lang={lang} align="start" />
          </div>
        )}

        {/* Content */}
        <div
          className="account-col account-col--wide"
          style={{ padding: '10px 20px 48px' }}
          data-testid="shared-memories-main"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-white/50 text-sm">{error}</p>
              <button
                onClick={loadMemories}
                className="mt-3 text-xs text-[#D4AF37] hover:underline"
              >
                {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
              </button>
            </div>
          ) : totalMemories === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                <Heart className="w-6 h-6 text-white/30" />
              </div>
              <p 
                className="text-white/40 text-sm"
                style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}
              >
                {lang === 'es' 
                  ? 'Aún no hay recuerdos compartidos. Los recuerdos se acumulan a través de la conversación.'
                  : 'No shared memories yet. Memories accumulate through conversation.'}
              </p>
            </div>
          ) : (
            <>
              <FilterRow active={filter} onChange={setFilter} lang={lang} />

              {filter !== 'all' && filterCounts[filter] === 0 && (
                <p className="py-10 text-center text-[14px]"
                   style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic',
                            color: 'rgba(244, 239, 231, 0.42)' }}
                   data-testid="memory-filter-empty">
                  {emptyFilterLine[filter][lang === 'es' ? 'es' : 'en']}
                </p>
              )}

              {/* ── 1. WHAT ARVEXIS REMEMBERS ABOUT ME — primary, open ───────────────── */}
              <MemoryGroup
                id="remembers"
                activeFilter={filter}
                title={lang === 'es' ? 'Lo que Arvexis recuerda de mí' : 'What Arvexis remembers about me'}
                count={rememberItems.length + milestones.length + enduringThreads.length}
                defaultOpen
              >
                {rememberItems.length > 0 && (
                  <div className="space-y-2">
                    {rememberItems.map((item, idx) => (
                      <MemoryCard
                        key={item.item_id || idx}
                        memory={item}
                        type="remember"
                        lang={lang}
                        onForget={handleForget}
                        onCorrect={handleCorrect}
                        isDeleting={deletingId === (item.item_id || item.content)}
                        isFocused={!!focusItemId && item.item_id === focusItemId}
                      />
                    ))}
                  </div>
                )}
                {enduringThreads.length > 0 && (
                  <div className="space-y-2 mt-2" data-testid="enduring-threads-section">
                    {enduringThreads.map((thread, idx) => (
                      <MemoryCard
                        key={thread.thread_id || idx}
                        memory={{ thread: thread.topic, ...thread }}
                        type="thread"
                        lang={lang}
                        onForget={handleForget}
                        isDeleting={deletingId === (thread.thread_id || thread.topic)}
                        isFocused={!!focusItemId && thread.thread_id === focusItemId}
                      />
                    ))}
                  </div>
                )}
                {milestones.length > 0 && (
                  <>
                    <div className="space-y-2 mt-2" data-testid="milestones-section">
                      {milestones.map((m, idx) => (
                        <MemoryCard
                          key={m.milestone_id || idx}
                          memory={{ content: m.summary, reason: m.kind, ...m }}
                          type="remember"
                          lang={lang}
                          onForget={handleForget}
                          onCorrect={handleCorrect}
                          isDeleting={deletingId === (m.milestone_id || m.summary)}
                          isFocused={!!focusItemId && m.milestone_id === focusItemId}
                        />
                      ))}
                    </div>
                  </>
                )}
              </MemoryGroup>

              {/* ── 2. WHAT'S GOING ON RIGHT NOW — subordinate, collapsed ────────────── */}
              <MemoryGroup
                id="current"
                activeFilter={filter}
                title={lang === 'es' ? 'Lo que está pasando ahora' : "What's going on right now"}
                subtitle={lang === 'es'
                  ? 'Cosas que situaste en un momento concreto.'
                  : 'Things you placed in a particular stretch of time.'}
                count={currentThreads.length}
              >
                <div className="space-y-2" data-testid="current-threads-section">
                  {currentThreads.map((thread, idx) => (
                    <MemoryCard
                      key={thread.thread_id || idx}
                      memory={{ thread: thread.topic, ...thread }}
                      type="thread"
                      lang={lang}
                      onForget={handleForget}
                      isDeleting={deletingId === (thread.thread_id || thread.topic)}
                      isFocused={!!focusItemId && thread.thread_id === focusItemId}
                    />
                  ))}
                </div>
              </MemoryGroup>

              {/* ── 3. FROM YOUR DREAMS — kept apart from waking life ────────────────── */}
              <MemoryGroup
                id="dreams"
                activeFilter={filter}
                title={lang === 'es' ? 'De tus sueños' : 'From your dreams'}
                subtitle={lang === 'es'
                  ? 'Evidencia de tu Diario de Sueños, separada de tu vida despierta.'
                  : 'Evidence from your Dream Journal, kept apart from your waking life.'}
                count={patterns.length + notes.length}
              >
                {patterns.length > 0 && (
                  <>
                    <SectionHeader
                      title={lang === 'es' ? 'Lo que vuelve una y otra vez' : 'What keeps coming back'}
                      count={patterns.length}
                      icon={Sparkles}
                    />
                    <p className="text-xs text-white/35 mb-2 px-1" data-testid="patterns-explainer">
                      {lang === 'es'
                        ? 'Contado desde los suenos que registraste. Si me equivoco, dimelo y dejo de usarlo.'
                        : 'Counted from the dreams you recorded. If I have it wrong, tell me and I stop using it.'}
                    </p>
                    <div className="space-y-2" data-testid="patterns-section">
                      {patterns.map((pattern) => (
                        <PatternCard
                          key={pattern.id}
                          pattern={pattern}
                          lang={lang}
                          onVerdict={handlePatternVerdict}
                          isBusy={deletingId === pattern.id}
                        />
                      ))}
                    </div>
                  </>
                )}
                {notes.length > 0 && (
                  <>
                    <SectionHeader
                      title={lang === 'es' ? 'Lo que anote por mi cuenta' : 'What I noted on my own'}
                      count={notes.length}
                      icon={MessageCircle}
                    />
                    <div className="space-y-2" data-testid="notes-section">
                      {notes.map((note) => (
                        <NoteCard
                          key={note.note_id}
                          note={note}
                          lang={lang}
                          onForget={handleForgetNote}
                          isBusy={deletingId === note.note_id}
                        />
                      ))}
                    </div>
                  </>
                )}
              </MemoryGroup>

              {/* ── 4. WHAT YOU ASKED HER TO FORGET — last ───────────────────────────── */}
              <MemoryGroup
                id="forgotten"
                activeFilter={filter}
                title={lang === 'es' ? 'Lo que pediste olvidar' : 'What you asked her to forget'}
                count={forgottenNotes.length + avoidItems.length}
              >
                {forgottenNotes.length > 0 && (
                  <>
                    <SectionHeader
                      title={lang === 'es' ? 'Notas que pediste olvidar' : 'Notes you asked me to forget'}
                      count={forgottenNotes.length}
                      icon={AlertTriangle}
                    />
                    <p className="text-xs text-white/35 mb-2 px-1" data-testid="notes-suppression-explainer">
                      {lang === 'es'
                        ? 'La nota se elimino. Solo queda un marcador para que no la vuelva a anotar por mi cuenta. Levantarlo no la recupera.'
                        : "The note is deleted. Only a marker remains so I don't note it again on my own. Lifting it does not bring the note back."}
                    </p>
                    <div className="space-y-2" data-testid="notes-suppression-section">
                      {forgottenNotes.map((item) => (
                        <div key={item.note_id}
                             className="p-3 rounded-xl flex items-center justify-between gap-3"
                             style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                             data-testid="note-suppression-item">
                          <span className="text-sm text-white/50">
                            {lang === 'es' ? 'Una nota que pediste olvidar' : 'A note you asked me to forget'}
                            {item.suppressed_at ? ` · ${String(item.suppressed_at).slice(0, 10)}` : ''}
                          </span>
                          <button onClick={() => handleLiftNoteSuppression(item)}
                                  disabled={deletingId === item.note_id}
                                  className="text-xs text-amber-400/60 hover:text-amber-400 disabled:opacity-40 whitespace-nowrap"
                                  data-testid="note-suppression-lift-btn">
                            {lang === 'es' ? 'Levantar' : 'Lift'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {avoidItems.length > 0 && (
                  <>
                    <SectionHeader 
                      title={lang === 'es' ? 'Temas que pediste olvidar' : 'Subjects you asked her to forget'} 
                      count={avoidItems.length}
                      icon={AlertTriangle}
                    />
                    <p className="text-xs text-white/35 mb-2 px-1" data-testid="suppression-explainer">
                      {lang === 'es'
                        ? 'El contenido se eliminó. Solo queda un marcador — sin tus palabras — para que no lo retome por su cuenta. Puedes levantarlo cuando quieras.'
                        : "The content is deleted. Only a marker remains — not your words — so she doesn't pick it back up on her own. You can lift it whenever you want."}
                    </p>
                    <div className="space-y-2" data-testid="suppression-section">
                      {(showAllAvoid ? avoidItems : avoidItems.slice(0, 8)).map((item, idx) => (
                        <div
                          key={item.item_id || idx}
                          className="p-3 rounded-xl flex items-center justify-between gap-3"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                          data-testid="suppression-item"
                        >
                          <span className="text-sm text-white/50">
                            {item.is_legacy_text && item.topic
                              ? item.topic
                              : (lang === 'es' ? 'Un tema que pediste olvidar' : 'A subject you asked her to forget')}
                          </span>
                          <button
                            onClick={() => handleLiftSuppression(item)}
                            disabled={deletingId === item.suppression_key}
                            className="text-xs text-amber-400/60 hover:text-amber-400 disabled:opacity-40 whitespace-nowrap"
                            data-testid="suppression-lift-btn"
                          >
                            {lang === 'es' ? 'Levantar' : 'Lift'}
                          </button>
                        </div>
                      ))}
                    </div>
                    {avoidItems.length > 8 && (
                      <button
                        onClick={() => setShowAllAvoid(s => !s)}
                        className="mt-2 text-xs text-white/40 hover:text-white/70"
                        data-testid="suppression-show-all-btn"
                      >
                        {showAllAvoid
                          ? (lang === 'es' ? 'Mostrar menos' : 'Show fewer')
                          : (lang === 'es' ? `Ver los ${avoidItems.length}` : `Show all ${avoidItems.length}`)}
                      </button>
                    )}
                  </>
                )}
              </MemoryGroup>
            </>
          )}

          {/* Add Memory Form */}
          <div className="mt-6">
            <AddMemoryForm 
              lang={lang} 
              onAdd={handleAddMemory}
              isAdding={isAdding}
            />
            {refusal && (
              <p role="status" className="mt-3 text-sm text-white/60" data-testid="add-memory-refusal">
                {refusal}
              </p>
            )}
          </div>

          {/* Footer note */}
          <p 
            className="text-center text-[10px] text-white/30 mt-6"
            style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}
          >
            {lang === 'es' 
              ? 'Puedes pedirle a Arvexis que recuerde o olvide algo en cualquier momento durante la conversación.'
              : 'You can ask Arvexis to remember or forget something at any time during conversation.'}
          </p>
        </div>
      </DreamsShell>

      {/* 6C-5 — the correction surface */}
      {correcting && (
        <CorrectionSheet
          memory={correcting}
          lang={lang}
          isSaving={!!deletingId}
          onCancel={() => setCorrecting(null)}
          onSubmit={submitCorrection}
        />
      )}

      {/* Phase 6 — Forget asks first. UI safety step only: the source-data retention contract
          is untouched, and the existing Forget action runs unchanged on confirmation. */}
      {pendingForget && (
        <ForgetConfirm
          lang={lang}
          isWorking={!!deletingId}
          onCancel={() => setPendingForget(null)}
          onConfirm={() => {
            const target = pendingForget;
            setPendingForget(null);
            if (target.kind === 'note') executeForgetNote(target.memory);
            else executeForget(target.memory);
          }}
        />
      )}
    </motion.div>
  );
};

export default SharedMemories;
